// ----------      START   IMPORT  BLOCK        ----------
import { useCallback, useState, useMemo } from "react";
// Context
import { useUser } from "../Context/UserContext";
// Enums
import { RelationshipsEnum } from "../../Types/Enums/RelationshipsEnum";
// Types
import type { UserRead, ContactObject } from "../../Types/schema-helpers";
// ----------      END   IMPORT  BLOCK        ----------

export type EmergencyContact = ContactObject;
export type ContactFields = Pick<UserRead, "phone_numbers" | "emergency_contacts">;

export const useUserContact = () => {
  const { user, updateUserProfile } = useUser();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const userContact: ContactFields | null = useMemo(() => {
    if (!user) return null;
    return {
      phone_numbers: user.phone_numbers ?? [],
      emergency_contacts: user.emergency_contacts ?? [],
    };
  }, [user?.phone_numbers, user?.emergency_contacts]);

  const readContact = useCallback(async (): Promise<boolean> => {
    return Boolean(user);
  }, [user]);

  const updateContact = useCallback(
    async (updatedData: ContactFields): Promise<boolean> => {
      setIsLoading(true);
      setApiError(null);
      try {
        // Map relationship strictly to RelationshipsEnum
        const sanitizedEmergencyContacts: ContactObject[] = (updatedData.emergency_contacts ?? []).map((ec) => {
          const relValue = typeof ec.relationship === "number" 
            ? ec.relationship 
            : Number(ec.relationship);

          const validRelationship: RelationshipsEnum = 
            relValue in RelationshipsEnum ? relValue : RelationshipsEnum.OTHER;

          return {
            name: ec.name || "",
            phone: ec.phone || "",
            relationship: validRelationship as unknown as ContactObject["relationship"],
          };
        });

        await updateUserProfile({
          contact: {
            phone_numbers: updatedData.phone_numbers ?? [],
          },
          emergency_contacts: sanitizedEmergencyContacts,
        });

        return true;
      } catch (error: any) {
        console.error("Error updating user contact:", error);

        let message = "Failed to save contact updates.";
        if (typeof error?.response?.data?.detail === "string") {
          message = error.response.data.detail;
        } else if (Array.isArray(error?.response?.data?.detail)) {
          message = error.response.data.detail
            .map((e: any) => `${e.loc?.slice(-1)[0] || "field"}: ${e.msg}`)
            .join(", ");
        } else if (error?.message) {
          message = error.message;
        }

        setApiError(message);
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [updateUserProfile]
  );

  return {
    userContact,
    isLoading,
    apiError,
    readContact,
    updateContact,
  };
};